import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import { APP_SCREENSHOTS } from './landingData';

// Why-this-matters copy paired with each screenshot. Kept here (not in
// landingData.ts) because it's tightly coupled to the scroll-narrative UX
// of this single component.
const WHY_COPY: Record<string, { heading: string; body: string }> = {
  dashboard: {
    heading: 'Everything at a glance',
    body: "Stop juggling four apps and a spreadsheet. Your training, nutrition, fridge and walking all live on one home screen — so the next right action is always one tap away. Less friction means more consistency.",
  },
  'gym-finder': {
    heading: 'Never miss a workout on the road',
    body: "Travel is one of the most common reasons people break their streak. Find a gym anywhere in the world, see ratings, opening hours and a 24/7-access filter — so your routine survives every business trip and vacation.",
  },
  training: {
    heading: 'Log every rep without breaking flow',
    body: "Pen-and-paper logs get lost. Memory is unreliable. Our gym tracker is built for between-set tapping — guided sets, smart weight suggestions, plate calculator, video form cues. Progressive overload becomes automatic.",
  },
  nutrition: {
    heading: 'Track meals in seconds',
    body: "Search 2.9M+ foods, snap a photo of any meal or restaurant menu, and let our AI estimate calories and macros in seconds. Barcodes, custom recipes, and a per-day macro ring keep you honest without the chore.",
  },
  walking: {
    heading: 'Hit your steps without the guilt',
    body: "Walking is the most under-rated fat-loss tool. Set a daily goal, track interval walking sessions, and watch your calorie deficit grow without the gym bag. GPS-tracked routes prove every step.",
  },
  statistics: {
    heading: 'See the progress others miss',
    body: "Performance score, volume index, milestone tracking and 6-month trend curves. The numbers that tell you whether last month actually worked — or whether you're spinning. No gut feelings, just data.",
  },
  'ai-planner': {
    heading: 'A workout plan built for YOU',
    body: "Generic 5-day-split PDFs ignore your equipment, schedule and injuries. Our AI builds a plan from your goals, your gym, your time — and adjusts every week based on what you actually lift. Like a coach, without the $200/hr.",
  },
  supplements: {
    heading: 'Your daily protocol on autopilot',
    body: "Log every supplement — daily, biweekly (Vit D&K), or monthly — and get smart reminders on the exact days each one is due. Adherence stats, take-together warnings and cycle tracking keep your stack working as intended.",
  },
};

const COUNT = APP_SCREENSHOTS.length;

export function ScreenshotsGallery() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const cooldownRef = useRef(false);
  const wheelAccumRef = useRef(0);
  const touchStartY = useRef<number | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const scroll = (dir: number) => {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  const advance = (dir: 1 | -1) => {
    setActiveIdx(prev => {
      const next = prev + dir;
      if (next < 0 || next >= COUNT) return prev;
      return next;
    });
  };

  // Returns true only when the carousel is well-centered: either the box
  // center is within ±15% of the viewport center, OR the box fully covers
  // the viewport (tall box on a short viewport). This prevents the wheel
  // hijack from kicking in while the box is still entering or leaving the
  // viewport — which was cutting off the text panel.
  const isWellCentered = (box: HTMLElement): boolean => {
    const rect = box.getBoundingClientRect();
    const vh = window.innerHeight;
    const fullyCovers = rect.top <= 0 && rect.bottom >= vh;
    if (fullyCovers) return true;
    const boxCenter = rect.top + rect.height / 2;
    const viewportCenter = vh / 2;
    return Math.abs(boxCenter - viewportCenter) < vh * 0.15;
  };

  // Wheel handler: only kicks in once the box is centered in the viewport.
  // Wheel deltas accumulate so tiny trackpad twitches don't advance slides;
  // we only fire after the user has clearly committed (≥ 120px cumulative
  // scroll). At first/last slide, scrolling past the boundary releases
  // control back to the page.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;

    const onWheel = (e: WheelEvent) => {
      if (!isWellCentered(box)) {
        wheelAccumRef.current = 0;
        return;
      }

      const dir = e.deltaY > 0 ? 1 : -1;
      // At boundary, let the page scroll naturally.
      if ((dir === 1 && activeIdx >= COUNT - 1) || (dir === -1 && activeIdx <= 0)) {
        wheelAccumRef.current = 0;
        return;
      }

      e.preventDefault();
      if (cooldownRef.current) return;

      // Reset accumulator if direction reverses.
      if (Math.sign(wheelAccumRef.current) !== Math.sign(e.deltaY) && wheelAccumRef.current !== 0) {
        wheelAccumRef.current = 0;
      }
      wheelAccumRef.current += e.deltaY;

      if (Math.abs(wheelAccumRef.current) < 120) return;

      wheelAccumRef.current = 0;
      cooldownRef.current = true;
      advance(dir as 1 | -1);
      // Long cooldown so trackpad inertia doesn't blast through slides.
      setTimeout(() => { cooldownRef.current = false; }, 900);
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [activeIdx]);

  // Touch swipe support for mouse-less desktops / touch screens.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    const onTouchStart = (e: TouchEvent) => { touchStartY.current = e.touches[0].clientY; };
    const onTouchMove = (e: TouchEvent) => {
      if (touchStartY.current == null) return;
      const dy = touchStartY.current - e.touches[0].clientY;
      if (Math.abs(dy) < 50) return;
      const dir = dy > 0 ? 1 : -1;
      if ((dir === 1 && activeIdx >= COUNT - 1) || (dir === -1 && activeIdx <= 0)) return;
      e.preventDefault();
      touchStartY.current = null;
      advance(dir as 1 | -1);
    };
    box.addEventListener('touchstart', onTouchStart, { passive: true });
    box.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      box.removeEventListener('touchstart', onTouchStart);
      box.removeEventListener('touchmove', onTouchMove);
    };
  }, [activeIdx]);

  // Keyboard support: ArrowUp / ArrowDown (and Left/Right) advance slides
  // when the carousel is well-centered in the viewport. Mirrors the wheel
  // gating so we don't hijack arrow keys while the user is reading other
  // sections. At the first/last slide we release control so the page scrolls.
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return;

    const onKeyDown = (e: KeyboardEvent) => {
      const isNext = e.key === 'ArrowDown' || e.key === 'ArrowRight' || e.key === 'PageDown';
      const isPrev = e.key === 'ArrowUp' || e.key === 'ArrowLeft' || e.key === 'PageUp';
      if (!isNext && !isPrev) return;

      // Ignore key events typed inside form fields / contenteditable.
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable) return;
      }

      if (!isWellCentered(box)) return;

      const dir: 1 | -1 = isNext ? 1 : -1;
      if ((dir === 1 && activeIdx >= COUNT - 1) || (dir === -1 && activeIdx <= 0)) return;

      e.preventDefault();
      if (cooldownRef.current) return;
      cooldownRef.current = true;
      advance(dir);
      setTimeout(() => { cooldownRef.current = false; }, 350);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeIdx]);

  return (
    <section className="pt-20 pb-8 px-6 sm:px-8" id="screenshots">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-headline font-bold tracking-tighter text-[#f1f3fc]">
            Why use Fitness&amp;me
          </h2>
        </div>

        {/* Mobile Gallery — phone screenshots carousel (unchanged) */}
        <div className="relative md:hidden">
          <button onClick={() => scroll(-1)} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-[#1b2028] border border-[#20262f] rounded-full flex items-center justify-center text-[#a8abb3] hover:text-white transition-colors shadow-lg" aria-label="Scroll left" data-testid="screenshots-scroll-left">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div ref={scrollRef} className="flex gap-6 overflow-x-auto px-14 pb-4 snap-x snap-mandatory" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {APP_SCREENSHOTS.map(screenshot => {
              const why = WHY_COPY[screenshot.id];
              // On mobile, several slots show a different screen than desktop,
              // so allow overrides from the data layer.
              const mLabel = (screenshot as any).mobileLabel || screenshot.label;
              const mBody = (screenshot as any).mobileBody || why?.body;
              return (
                <div key={screenshot.id} className="flex-shrink-0 snap-center w-72">
                  <div className="p-2 bg-[#151a21] rounded-[1.5rem] border border-[#44484f]/20 shadow-xl">
                    <img src={screenshot.url} alt={mLabel} className="w-full h-[440px] sm:h-[500px] rounded-[1.2rem] object-cover object-top" loading="lazy" />
                  </div>
                  <p className="text-base text-[#f1f3fc] mt-3 text-center font-bold font-headline tracking-tight">{mLabel}</p>
                  {mBody && (<p className="text-sm text-[#a8abb3] mt-2 text-center leading-relaxed">{mBody}</p>)}
                </div>
              );
            })}
          </div>
          <button onClick={() => scroll(1)} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-[#1b2028] border border-[#20262f] rounded-full flex items-center justify-center text-[#a8abb3] hover:text-white transition-colors shadow-lg" aria-label="Scroll right" data-testid="screenshots-scroll-right">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Desktop / Tablet: self-contained carousel box. Wheel/touch INSIDE
            the box advances slides without moving the page. */}
        <div
          ref={boxRef}
          className="hidden md:block relative max-w-6xl mx-auto rounded-[32px] border border-[#44484f]/20 bg-[#1b2028]/40 backdrop-blur-sm shadow-2xl overflow-hidden"
          data-testid="screenshots-carousel-box"
        >
          <div className="grid md:grid-cols-12 md:gap-8 lg:gap-12 items-center p-6 lg:p-10" style={{ minHeight: 560 }}>
            {/* Left: desktop screenshot in browser frame */}
            <div className="md:col-span-7 lg:col-span-7">
              <div className="relative rounded-2xl bg-[#151a21] border border-[#44484f]/20 shadow-xl overflow-hidden">
                <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-[#20262f] bg-[#0f141a]">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
                  <span className="ml-3 text-[10px] text-[#a8abb3] truncate">fitnessand.me</span>
                </div>
                <div className="relative w-full bg-[#0f141a]" style={{ aspectRatio: '4 / 3' }}>
                  {APP_SCREENSHOTS.map((s, i) => (
                    <img
                      key={s.id}
                      src={s.desktopUrl}
                      alt={s.label}
                      className={`absolute inset-0 w-full h-full object-contain object-top transition-opacity duration-400 ${i === activeIdx ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                      data-testid={`screenshots-image-${s.id}`}
                      loading="lazy"
                      decoding="async"
                      fetchPriority="low"
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Right: text with up/down arrows */}
            <div className="md:col-span-5 lg:col-span-5 relative">
              <div className="relative min-h-[280px]">
                {APP_SCREENSHOTS.map((s, i) => {
                  const why = WHY_COPY[s.id];
                  return (
                    <div
                      key={s.id}
                      className={`transition-all duration-400 ${i === activeIdx ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none absolute inset-0'}`}
                      data-testid={`screenshots-panel-${s.id}`}
                    >
                      <span className="text-[10px] uppercase tracking-[0.3em] text-[#cafd00] font-bold block mb-3">
                        {String(i + 1).padStart(2, '0')} / {String(COUNT).padStart(2, '0')} · {s.label}
                      </span>
                      <h3 className="text-2xl lg:text-3xl xl:text-4xl font-headline font-bold tracking-tight text-[#f1f3fc] mb-4 leading-tight">
                        {why?.heading || s.label}
                      </h3>
                      <p className="text-base lg:text-lg text-[#a8abb3] leading-relaxed">
                        {why?.body || s.description}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Up/Down nav + dots */}
              <div className="mt-8 flex items-center gap-4">
                <button
                  onClick={() => advance(-1)}
                  disabled={activeIdx === 0}
                  className="w-10 h-10 rounded-full border border-[#44484f]/40 bg-[#0f141a] text-[#f1f3fc] flex items-center justify-center hover:bg-[#20262f] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous"
                  data-testid="screenshots-prev"
                >
                  <ChevronUp className="h-5 w-5" />
                </button>
                <button
                  onClick={() => advance(1)}
                  disabled={activeIdx === COUNT - 1}
                  className="w-10 h-10 rounded-full border border-[#44484f]/40 bg-[#0f141a] text-[#f1f3fc] flex items-center justify-center hover:bg-[#20262f] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next"
                  data-testid="screenshots-next"
                >
                  <ChevronDown className="h-5 w-5" />
                </button>
                <div className="flex gap-1.5 ml-2">
                  {APP_SCREENSHOTS.map((s, i) => (
                    // Pagination dots — visual size stays at h-1.5; the
                    // outer button gets explicit 24×24px padding so the
                    // hit-target meets the WCAG / Lighthouse minimum.
                    <button
                      key={s.id}
                      onClick={() => setActiveIdx(i)}
                      className="p-2 flex items-center justify-center group"
                      aria-label={`Go to ${s.label}`}
                      data-testid={`screenshots-dot-${s.id}`}
                    >
                      <span
                        className={`h-1.5 rounded-full transition-all block ${i === activeIdx ? 'w-8 bg-[#cafd00]' : 'w-4 bg-[#20262f] group-hover:bg-[#44484f]'}`}
                        aria-hidden="true"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
